import { QueryClient, QueryFunction } from "@tanstack/react-query";
import { registerShopFloorMutationDefaults } from "./shop-floor-offline";

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    throw new Error(`${res.status}: ${text}`);
  }
}

export function parseApiError(error: any, fallback = "An error occurred"): string {
  const msg = error?.message || String(error);
  try {
    const jsonPart = msg.replace(/^\d+:\s*/, "");
    const parsed = JSON.parse(jsonPart);
    if (parsed.message) return parsed.message;
  } catch {}
  return fallback;
}

/**
 * Detect whether an API error is a RECOVERABLE 409 — a version conflict (from
 * updateWithLock, carrying a `conflict` payload) or a schedule-slot overlap
 * collision (carrying the server's `slotConflict: true` marker). HUB-522:
 * schedule mutation handlers use this to declare toast severity from response
 * STRUCTURE (recoverable conflict → 8s warning, anything else → sticky error).
 *
 * Fail-safe by design (Bugbot PR #253, rounds 2–3): any 409 that carries
 * NEITHER marker — "Run N is already scheduled", the split whole-order-schedule
 * rejection, email-in-flight guards, unknown future 409s — stays a sticky
 * error. Recoverability is opt-in via an explicit server marker, never inferred
 * from wording and never assumed for unrecognized responses.
 */
export function isConflict409(error: any): boolean {
  const msg = error?.message || String(error);
  if (!msg.startsWith("409:")) return false;
  try {
    const jsonPart = msg.replace(/^\d+:\s*/, "");
    const parsed = JSON.parse(jsonPart);
    return parsed?.slotConflict === true || !!parsed?.conflict;
  } catch {
    // Unparseable 409 body carries no marker: fail safe to sticky.
    return false;
  }
}

/**
 * Detect whether an API error is a 409 version conflict (from updateWithLock).
 * Schedule-overlap 409s have only `message`; version conflicts have `conflict`.
 */
export function isVersionConflict(error: any): boolean {
  const msg = error?.message || String(error);
  if (!msg.startsWith("409:")) return false;
  try {
    const jsonPart = msg.replace(/^\d+:\s*/, "");
    const parsed = JSON.parse(jsonPart);
    return !!parsed.conflict;
  } catch {}
  return false;
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
  opts?: { idempotencyKey?: string; signal?: AbortSignal },
): Promise<Response> {
  const headers: Record<string, string> = {};
  if (data) headers["Content-Type"] = "application/json";
  // Idempotency-Key (PRO-161): stable per submission attempt so retries/double
  // clicks dedupe server-side. Sent as a header so it survives Zod body parsing.
  if (opts?.idempotencyKey) headers["Idempotency-Key"] = opts.idempotencyKey;

  const res = await fetch(url, {
    method,
    headers,
    body: data ? JSON.stringify(data) : undefined,
    credentials: "include",
    ...(opts?.signal ? { signal: opts.signal } : {}),
  });

  await throwIfResNotOk(res);
  return res;
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey, signal }) => {
    const res = await fetch(queryKey.join("/") as string, {
      credentials: "include",
      signal,
    });

    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      return null;
    }

    await throwIfResNotOk(res);
    return await res.json();
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: true,
      staleTime: 30 * 1000, // 30 seconds — allow background refetch for multi-user freshness
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
});

registerShopFloorMutationDefaults(queryClient);
